import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi, apiResolver } from '@/api/useApi';
import type {
  FieldSyncConfiguration,
  AvailableField,
  CreateFieldSyncConfigurationDto,
  UpdateFieldSyncConfigurationDto,
  MappingType,
  SyncResult,
} from './FieldSync.model';

const FIELD_SYNC_KEYS = {
  all: ['field-sync'] as const,
  configurations: () => [...FIELD_SYNC_KEYS.all, 'configurations'] as const,
  availableFields: (mappingType: MappingType) =>
    [...FIELD_SYNC_KEYS.all, 'available-fields', mappingType] as const,
};

// API methods hook
export function useFieldSyncApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getConfigurations: async () =>
        apiResolver<FieldSyncConfiguration[]>(api.get('/platform/field-sync/configurations')),

      getAvailableFields: async (mappingType: MappingType) =>
        apiResolver<AvailableField[]>(
          api.post('/platform/field-sync/available-fields', { mappingType })
        ),

      createConfiguration: async (data: CreateFieldSyncConfigurationDto) =>
        apiResolver<FieldSyncConfiguration>(
          api.post('/platform/field-sync/configurations', data)
        ),

      updateConfiguration: async (id: string, data: UpdateFieldSyncConfigurationDto) =>
        apiResolver<FieldSyncConfiguration>(
          api.put(`/platform/field-sync/configurations/${id}`, data)
        ),

      deleteConfiguration: async (id: string) =>
        apiResolver<{ id: string }>(api.delete(`/platform/field-sync/configurations/${id}`)),

      triggerSync: async (ticketId: string) =>
        apiResolver<SyncResult[]>(api.post(`/platform/field-sync/sync/${ticketId}`)),
    }),
    [api]
  );
}

// Get all configurations
export function useGetFieldSyncConfigurations() {
  const { getConfigurations } = useFieldSyncApi();

  return useQuery({
    queryKey: FIELD_SYNC_KEYS.configurations(),
    queryFn: () => getConfigurations(),
  });
}

// Get available fields for a mapping type
export function useGetAvailableFields(mappingType: MappingType | null) {
  const { getAvailableFields } = useFieldSyncApi();

  return useQuery({
    queryKey: mappingType ? FIELD_SYNC_KEYS.availableFields(mappingType) : ['disabled'],
    queryFn: () => (mappingType ? getAvailableFields(mappingType) : Promise.resolve([])),
    enabled: !!mappingType,
  });
}

// Create configuration
export function useCreateFieldSyncConfiguration() {
  const queryClient = useQueryClient();
  const { createConfiguration } = useFieldSyncApi();

  return useMutation({
    mutationFn: (data: CreateFieldSyncConfigurationDto) => createConfiguration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_SYNC_KEYS.configurations() });
    },
  });
}

// Update configuration
export function useUpdateFieldSyncConfiguration() {
  const queryClient = useQueryClient();
  const { updateConfiguration } = useFieldSyncApi();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFieldSyncConfigurationDto }) =>
      updateConfiguration(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_SYNC_KEYS.configurations() });
    },
  });
}

// Delete configuration
export function useDeleteFieldSyncConfiguration() {
  const queryClient = useQueryClient();
  const { deleteConfiguration } = useFieldSyncApi();

  return useMutation({
    mutationFn: (id: string) => deleteConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIELD_SYNC_KEYS.configurations() });
    },
  });
}

// Manual sync trigger
export function useTriggerManualSync() {
  const { triggerSync } = useFieldSyncApi();

  return useMutation({
    mutationFn: (ticketId: string) => triggerSync(ticketId),
  });
}
